import { ReactNode } from 'react';
import { RouterProvider } from 'react-aria-components';
import { useHref, useNavigate } from 'react-router-dom';

/**
 * Routes react-aria's `href` navigation through react-router: the design-system `Link`,
 * `LinkButton`, `EntityCard` and anything else built on a react-aria link. Without it react-aria
 * leaves every click to the browser, so an in-app link reloads the whole app and loses in-memory
 * state such as the profile Back trail (PLT-6888).
 *
 * React-aria still hands external links (another origin, `target`, `download`) and
 * modifier-clicks to the browser, so new-tab and download behaviour is unchanged.
 */
export function AriaRouterProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <RouterProvider
      navigate={(to, options) => {
        navigate(to, options);
      }}
      useHref={useRouterHref}
    >
      {children}
    </RouterProvider>
  );
}

const ABSOLUTE_URL = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;

/**
 * React-router's `useHref` treats every string as a path, so `https://pay.stripe.com/…` would
 * render as `/current/route/https:/pay.stripe.com/…`. An absolute URL (or `mailto:` and the
 * like) is left as written.
 */
function useRouterHref(href: string): string {
  // Called unconditionally: it is a hook.
  const resolved = useHref(href);
  return ABSOLUTE_URL.test(href) ? href : resolved;
}
